import { useState, useEffect } from 'react';
import { useAuth } from '../context/Auth/AuthContext';
import { pointsService, PointsBalance, PointTransaction, PointsCostInfo } from '../services/pointsService';

export const usePointsState = () => {
  const { user, token, isAuthenticated } = useAuth();
  const [balance, setBalance] = useState<PointsBalance | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<PointTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [planType, setPlanType] = useState<'points' | 'unlimited' | 'traditional' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize when user authenticates
  useEffect(() => {
    if (isAuthenticated && user && token) {
      initializePointsData();
    } else {
      resetState();
    }
  }, [isAuthenticated, user, token]);

  const resetState = () => {
    setBalance(null);
    setRecentTransactions([]);
    setIsEnabled(false);
    setPlanType(null);
    setError(null);
  };

  const initializePointsData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const pointsData = await pointsService.getPointsData();
      
      if (pointsData.available) {
        setIsEnabled(true);
        setBalance(pointsData.balance);
        setRecentTransactions(pointsData.recentTransactions || []);
        setPlanType(pointsData.balance?.isUnlimited ? 'unlimited' : 'points');
      } else {
        setIsEnabled(false);
        setPlanType('traditional');
      }
    } catch (err) {
      console.error('Error initializing points:', err);
      setError('Failed to load points data');
      setIsEnabled(false);
      setPlanType('traditional');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshBalance = async () => {
    if (!isEnabled) return;

    try {
      setError(null);
      const pointsData = await pointsService.getPointsData();
      if (pointsData.available) {
        setBalance(pointsData.balance);
      }
    } catch (err) {
      setError('Failed to refresh balance');
    }
  };

  const refreshTransactions = async () => {
    if (!isEnabled) return;

    try {
      setError(null);
      const transactions = await pointsService.getTransactionHistory(20);
      setRecentTransactions(transactions);
    } catch (err) {
      setError('Failed to refresh transactions');
    }
  };

  const checkCost = async (featureType: string, amount: number = 1): Promise<PointsCostInfo | null> => {
    if (!isEnabled || !balance) return null;

    try {
      setError(null);
      return await pointsService.calculateCost(featureType, amount);
    } catch (err) {
      setError('Failed to check cost');
      return null;
    }
  };

  const clearError = () => setError(null);

  return {
    balance,
    recentTransactions,
    isLoading,
    isEnabled,
    planType,
    error,
    refreshBalance,
    refreshTransactions,
    checkCost,
    clearError,
  };
};
